import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { maskPhone } from "@/lib/phone";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useMasterData } from "@/hooks/use-master-data";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft } from "lucide-react";

export default function NewLead() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = ["super_admin", "hq_admin"].includes(user?.role);
  const isTelecaller = user?.role === "telecaller";
  const { stages: LEAD_STAGES, sources: SOURCES, statuses: LEAD_STATUSES, subStatuses: LEAD_SUB_STATUSES, categories: LEAD_CATEGORIES, concerns: LEAD_CONCERNS, services: SERVICES, subSources, locations: LOCATIONS } = useMasterData();

  const empty = {
    name: "", mobile: "", whatsapp: "", alt_mobile: "", email: "",
    city: "", location: "", state: "", country: "", gender: "", dob: "", age: "", language: "",
    interested_service: "", category: "", concern: "",
    source: "", sub_source: "", status: "new_lead", sub_status: "", remark: "",
    stage: "warm", branch_id: "", assigned_to: isTelecaller ? (user?.id || "") : "", notes: "",
  };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // A picked DOB fills Age so the two can't disagree; Age stays editable for
  // callers who only give an age.
  const setDob = (v) => {
    const b = new Date(v);
    if (!v || isNaN(b)) { set("dob", v); return; }
    const t = new Date();
    const age = t.getFullYear() - b.getFullYear() - ((t.getMonth() < b.getMonth() || (t.getMonth() === b.getMonth() && t.getDate() < b.getDate())) ? 1 : 0);
    setForm((f) => ({ ...f, dob: v, age: age >= 0 ? String(age) : f.age }));
  };
  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    api.get("/branches").then((r) => {
      let list = r.data;
      if ((user?.branch_ids || []).length && ["branch_manager", "receptionist"].includes(user?.role)) {
        list = list.filter((b) => (user.branch_ids || []).includes(b.id));
      }
      setBranches(list);
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    // Telecallers can only create leads assigned to themselves — no user list needed.
    if (isTelecaller) return;
    const params = { department: "pre_sales" };
    if (form.branch_id) params.branch_id = form.branch_id;
    api.get("/users", { params }).then((r) => setUsers(r.data)).catch(() => {});
    set("assigned_to", "");
  }, [form.branch_id, isTelecaller]);

  const submit = async () => {
    if (!form.mobile) { toast.error("Mobile number is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        dob: form.dob || null,
        age: form.age ? parseInt(form.age) : null,
        branch_id: form.branch_id || null,
        assigned_to: form.assigned_to || null,
      };
      await api.post("/leads", payload);
      toast.success("Lead created");
      navigate("/leads");
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Duplicate: the person is already in the CRM — nothing was created or
      // changed. Tell the user who owns the existing lead (stay on the form).
      if (e.response?.status === 409 && detail?.code === "duplicate_lead") {
        const who = detail.assigned_to_name
          ? `assigned to ${detail.assigned_to_name}`
          : "not assigned to anyone yet";
        toast.error(
          `This lead already exists (${detail.name || maskPhone(detail.mobile)}) — ${who}.`,
          { duration: 6000 },
        );
      } else {
        toast.error(formatApiError(detail));
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <button onClick={() => navigate("/leads")} className="flex items-center gap-2 text-sm font-medium text-navy hover:underline">
        <ArrowLeft className="h-4 w-4" /> Back to leads
      </button>

      <div className="rounded-xl border border-navy/10 bg-white p-6 shadow-sm">
        <h2 className="font-display text-xl font-semibold text-navy mb-6">New Lead</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Contact */}
          <SectionLabel label="Contact" />
          <Field label="Mobile *"><Input value={form.mobile} onChange={(e) => set("mobile", e.target.value)} data-testid="lead-mobile-input" /></Field>
          <Field label="Name"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="lead-name-input" /></Field>
          <Field label="WhatsApp"><Input value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} /></Field>
          <Field label="Alternate Phone"><Input value={form.alt_mobile} onChange={(e) => set("alt_mobile", e.target.value)} /></Field>
          <Field label="Email"><Input value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
          <Field label="City"><Input value={form.city} onChange={(e) => set("city", e.target.value)} /></Field>
          <Field label="Location">
            <Select value={form.location} onValueChange={(v) => set("location", v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{LOCATIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label="State"><Input value={form.state} onChange={(e) => set("state", e.target.value)} /></Field>
          <Field label="Country"><Input value={form.country} onChange={(e) => set("country", e.target.value)} /></Field>
          <Field label="Date of Birth"><Input type="date" max={today} value={form.dob} onChange={(e) => setDob(e.target.value)} data-testid="lead-dob-input" /></Field>
          <Field label="Age"><Input type="number" value={form.age} onChange={(e) => set("age", e.target.value)} /></Field>
          <Field label="Gender">
            <Select value={form.gender} onValueChange={(v) => set("gender", v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent><SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent></Select>
          </Field>
          <Field label="Language"><Input value={form.language} onChange={(e) => set("language", e.target.value)} /></Field>

          {/* Classification */}
          <SectionLabel label="Classification" />
          <Field label="Category">
            <Select value={form.category} onValueChange={(v) => set("category", v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{LEAD_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label="Concern">
            <Select value={form.concern} onValueChange={(v) => set("concern", v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{LEAD_CONCERNS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label="Interested Service">
            <Select value={form.interested_service} onValueChange={(v) => set("interested_service", v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
          </Field>

          {/* Pipeline */}
          <SectionLabel label="Pipeline" />
          <Field label="Source">
            <Select value={form.source} onValueChange={(v) => { set("source", v); set("sub_source", ""); }}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label="Sub Source">
            <Select value={form.sub_source} onValueChange={(v) => set("sub_source", v)}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {subSources.filter(s => s.parent === form.source).map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Status">
            <Select value={form.status} onValueChange={(v) => set("status", v)}><SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label="Sub Status">
            <Select value={form.sub_status} onValueChange={(v) => set("sub_status", v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{LEAD_SUB_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label="Stage">
            <Select value={form.stage} onValueChange={(v) => set("stage", v)}><SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{LEAD_STAGES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent></Select>
          </Field>

          {/* Assignment */}
          <SectionLabel label="Assignment" />
          <Field label="Branch">
            <Select value={form.branch_id} onValueChange={(v) => set("branch_id", v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent></Select>
          </Field>
          {!isTelecaller && (
            <Field label="Assign To">
              <Select value={form.assigned_to} onValueChange={(v) => set("assigned_to", v)}><SelectTrigger><SelectValue placeholder={form.branch_id ? "Select" : "Select branch first"} /></SelectTrigger>
                <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent></Select>
            </Field>
          )}

          {/* Notes */}
          <div className="col-span-1 sm:col-span-2 lg:col-span-3">
            <Field label="Remark"><Input value={form.remark} onChange={(e) => set("remark", e.target.value)} placeholder="e.g. Appointment booked 2nd jan at 6:30pm" /></Field>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-3 justify-end">
          <Button variant="outline" onClick={() => navigate("/leads")}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-navy text-white hover:bg-navy-hover" data-testid="save-lead-btn">
            {saving ? "Saving…" : "Create Lead"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ label }) {
  return (
    <div className="col-span-1 sm:col-span-2 lg:col-span-3 mt-2 first:mt-0">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground border-b border-navy/10 pb-1">{label}</p>
    </div>
  );
}
